import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Typography,
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Divider,
    Card,
    CardContent,
    Skeleton,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    EmojiEvents as TrophyIcon,
    Stars as CoinsIcon,
    TrendingUp as LevelIcon,
    LocalFireDepartment as StreakIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { createGamificationService } from '../services/GamificationService';

const RankDisplay = ({ rank }) => {
    const theme = useTheme();
    
    const getRankProps = (rank) => {
        const colors = theme.palette.gamification;
        if (rank === 1) return { icon: '🥇', color: colors.gold, gradient: `linear-gradient(135deg, ${colors.gold}, ${theme.palette.mode === 'dark' ? '#FFA500' : '#d97706'})` };
        if (rank === 2) return { icon: '🥈', color: colors.silver, gradient: `linear-gradient(135deg, ${colors.silver}, ${theme.palette.mode === 'dark' ? '#808080' : '#6b7280'})` };
        if (rank === 3) return { icon: '🥉', color: colors.bronze, gradient: `linear-gradient(135deg, ${colors.bronze}, ${theme.palette.mode === 'dark' ? '#8B4513' : '#654321'})` };
        return { icon: `#${rank}`, color: theme.palette.text.secondary, gradient: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.divider})` };
    };

    const props = getRankProps(rank);

    return (
        <Avatar
            sx={{
                width: 40,
                height: 40,
                background: props.gradient,
                color: rank <= 3 ? 'white' : 'black',
                fontWeight: 'bold',
                fontSize: rank <= 3 ? '1.2rem' : '0.9rem',
                boxShadow: rank <= 3 ? 3 : 1,
            }}
        >
            {props.icon}
        </Avatar>
    );
};

const LeaderboardModal = ({ open, onClose, userId, accessToken }) => {
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        const loadLeaderboard = async () => {
            if (!open) return;

            try {
                setLoading(true);
                const gamificationService = createGamificationService(accessToken);
                const data = await gamificationService.getLeaderboard(20, userId);
                setLeaderboardData(data);
                setError(null);
            } catch (err) {
                console.error('Failed to load leaderboard:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadLeaderboard();
    }, [open, userId, accessToken]);

    const getDisplayName = (user) => {
        if (user.username) return user.username;
        if (user.display_name) return user.display_name;
        if (user.given_name && user.family_name) return `${user.given_name} ${user.family_name}`;
        if (user.given_name) return user.given_name;
        return user.email || 'Anonymous User';
    };

    const getInitials = (user) => {
        const name = getDisplayName(user);
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Defensive mapping for backend response
    const topUsers = leaderboardData?.entries || leaderboardData?.top_users || [];
    const currentUser = leaderboardData?.current_user_entry || leaderboardData?.current_user || null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                background: theme => theme.palette.background.gamification,
                color: 'white'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrophyIcon sx={{ mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Leaderboard
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ p: 3 }}>
                        {[...Array(10)].map((_, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                <Box sx={{ flexGrow: 1 }}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="40%" />
                                </Box>
                                <Skeleton variant="rectangular" width={60} height={24} />
                            </Box>
                        ))}
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="error">Error loading leaderboard: {error}</Typography>
                    </Box>
                ) : leaderboardData ? (
                    <>
                        {/* Current User Highlight (if not in top list) */}
                        {currentUser && !topUsers.some(u => u.user_id === userId) && (
                            <>
                                <Card sx={{ m: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                                    <CardContent sx={{ py: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                                            Your Position
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RankDisplay rank={currentUser.rank_position || currentUser.rank || 1} />
                                            <Box sx={{ ml: 2, flexGrow: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                    {getDisplayName(currentUser)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                    <Chip
                                                        icon={<LevelIcon />}
                                                        label={`Level ${currentUser.current_level || currentUser.level || 1}`}
                                                        size="small"
                                                        variant="filled"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                                                    />
                                                    <Chip
                                                        icon={<CoinsIcon />}
                                                        label={`${(currentUser.total_coins || currentUser.coins || 0).toLocaleString()}`}
                                                        size="small"
                                                        variant="filled"
                                                        sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
                                                    />
                                                </Box>
                                            </Box>
                                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                                {(currentUser.total_xp || 0).toLocaleString()} XP
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                                <Divider />
                            </>
                        )}

                        {/* Top Users Table */}
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Level</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>XP</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Coins</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Streak</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {topUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No leaderboard data available.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        topUsers.map((user, index) => (
                                            <TableRow
                                                key={user.user_id || user.id || index}
                                                sx={{
                                                    bgcolor: (user.user_id || user.id) === userId 
                                                        ? `${theme.palette.primary.main}20` 
                                                        : 'inherit',
                                                    '&:hover': { 
                                                        bgcolor: (user.user_id || user.id) === userId 
                                                            ? `${theme.palette.primary.main}30` 
                                                            : `${theme.palette.gamification.primary}10`,
                                                        transform: 'translateX(4px)',
                                                    },
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    borderLeft: (user.user_id || user.id) === userId 
                                                        ? `4px solid ${theme.palette.primary.main}` 
                                                        : '4px solid transparent',
                                                    animation: (user.user_id || user.id) === userId 
                                                        ? 'pulse 2s infinite' 
                                                        : 'none',
                                                    '@keyframes pulse': {
                                                        '0%': {
                                                            boxShadow: `0 0 0 0 ${theme.palette.primary.main}40`,
                                                        },
                                                        '70%': {
                                                            boxShadow: `0 0 0 10px transparent`,
                                                        },
                                                        '100%': {
                                                            boxShadow: `0 0 0 0 transparent`,
                                                        },
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <RankDisplay rank={user.rank_position || user.rank || index + 1} />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Avatar
                                                            sx={{
                                                                width: 32,
                                                                height: 32,
                                                                mr: 2,
                                                                bgcolor: (user.user_id || user.id) === userId ? 'primary.main' : 'grey.400',
                                                                fontSize: '0.8rem',
                                                            }}
                                                        >
                                                            {user.picture ? (
                                                                <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                getInitials(user)
                                                            )}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: (user.user_id || user.id) === userId ? 'bold' : 'normal' }}>
                                                                {getDisplayName(user)}
                                                                {(user.user_id || user.id) === userId && (
                                                                    <Chip
                                                                        label="You"
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ ml: 1, height: 20 }}
                                                                    />
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={<LevelIcon />}
                                                        label={user.current_level || user.level || 1}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {(user.total_xp || 0).toLocaleString()}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <CoinsIcon sx={{ fontSize: 16, color: 'orange', mr: 0.5 }} />
                                                        <Typography variant="body2">
                                                            {(user.total_coins || user.coins || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <StreakIcon sx={{ fontSize: 16, color: 'red', mr: 0.5 }} />
                                                        <Typography variant="body2">
                                                            {(user.streak_days || user.current_streak || 0)} days
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Statistics Footer */}
                        <Box sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50',
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                                🏆 Live leaderboard • Compete with fellow students!
                            </Typography>
                        </Box>
                    </>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export { LeaderboardModal };
